import { useEffect, useState } from 'react';
import { crearClub, editarClub } from '../../../services/clubsService';
import { uploadClubLogo } from '../../../services/cmsService';
import { useToast } from '../context/ToastContext';
import { REGIONES, getComunasPorRegion } from '../../../data/regionesYComunas';

/* =========================
   VALIDADORES
========================= */
const validarEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validarTelefono = (tel) =>
  /^[0-9]{8,12}$/.test(tel);

const validarRut = (rut) => {
  if (!rut) return true; // opcional
  const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 2) return false;

  const cuerpo = clean.slice(0, -1);
  const dv = clean.slice(-1);

  let suma = 0;
  let multiplo = 2;

  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += multiplo * parseInt(cuerpo[i], 10);
    multiplo = multiplo === 7 ? 2 : multiplo + 1;
  }

  const dvEsperado = 11 - (suma % 11);
  const dvFinal =
    dvEsperado === 11 ? '0' :
    dvEsperado === 10 ? 'K' :
    dvEsperado.toString();

  return dv === dvFinal;
};

/* =========================
   COMPONENTE
========================= */
const ClubFormModal = ({ club, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    nombre: '',
    rut_club: '',
    presidente_id: '',
    descripcion: '',
    logo_url: '',
    direccion: '',
    ciudad: '',
    region: '',
    telefono: '',
    email: '',
    sitio_web: '',
    estado: 'activo'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSubiendoLogo(true);
    try {
      const url = await uploadClubLogo(file);
      setForm((prev) => ({ ...prev, logo_url: url }));
      showToast('Logo cargado. Guarda el club para aplicarlo.', 'success');
    } catch (err) {
      showToast(err?.message || 'No se pudo subir el logo', 'error');
    } finally {
      setSubiendoLogo(false);
    }
  };

  useEffect(() => {
    if (club) {
      setForm({
        nombre: club.nombre ?? '',
        rut_club: club.rut_club ?? '',
        presidente_id: club.presidente_id ?? '',
        descripcion: club.descripcion ?? '',
        logo_url: club.logo_url ?? '',
        direccion: club.direccion ?? '',
        ciudad: club.ciudad ?? '',
        region: club.region ?? '',
        telefono: club.telefono ?? '',
        email: club.email ?? '',
        sitio_web: club.sitio_web ?? '',
        estado: club.estado ?? 'activo'
      });
    }
  }, [club]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'region') next.ciudad = '';
      return next;
    });
    setErrors({ ...errors, [name]: null });
  };

  const validarFormulario = () => {
    const newErrors = {};

    if (!form.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }

    if (form.email && !validarEmail(form.email)) {
      newErrors.email = 'Correo inválido';
    }

    if (form.telefono && !validarTelefono(form.telefono)) {
      newErrors.telefono = 'Teléfono inválido';
    }

    if (form.presidente_id && !validarRut(form.presidente_id)) {
      newErrors.presidente_id = 'RUT inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;

    setLoading(true);

    const payload = {
      ...form,
      presidente_id: form.presidente_id || null,
      logo_url: form.logo_url || null
    };

    try {
      if (club) {
        await editarClub(club.id, payload);
      } else {
        await crearClub(payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      showToast(err?.message || 'Error al guardar club', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <style>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-card {
          background: #ffffff;
          padding: 24px;
          border-radius: 12px;
          width: 800px;
          max-width: 95%;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full {
          grid-column: span 2;
        }

        label {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        input, textarea, select {
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid #d1d5db;
          font-size: 14px;
        }

        .error {
          font-size: 12px;
          color: #c62828;
          margin-top: 4px;
        }

        textarea {
          resize: vertical;
          min-height: 80px;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
          grid-column: span 2;
        }

        .btn-primary {
          padding: 8px 16px;
          background: #1a4d8c;
          color: #fff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }

        .btn-ghost {
          padding: 8px 16px;
          background: #f4f6f8;
          color: #111;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }
      `}</style>

      <div className="modal-card">
        <h3>{club ? 'Editar Club' : 'Crear Club'}</h3>

        <form onSubmit={handleSubmit} className="form-grid">

          <div className="form-group">
            <label>Nombre *</label>
            <input name="nombre" value={form.nombre} onChange={handleChange} />
            {errors.nombre && <span className="error">{errors.nombre}</span>}
          </div>

          <div className="form-group">
            <label>RUT Club</label>
            <input name="rut_club" value={form.rut_club} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>RUT Presidente</label>
            <input
              placeholder="12345678K"
              name="presidente_id"
              value={form.presidente_id}
              onChange={handleChange}
            />
            {errors.presidente_id && <span className="error">{errors.presidente_id}</span>}
          </div>

          <div className="form-group full">
            <label htmlFor="club-logo">Logo del club</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo del club"
                  style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ddd' }}
                />
              ) : (
                <span style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f1f1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                  Sin logo
                </span>
              )}
              <input id="club-logo" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleLogo} disabled={subiendoLogo} />
              {form.logo_url && (
                <button type="button" onClick={() => setForm((prev) => ({ ...prev, logo_url: '' }))} disabled={subiendoLogo}>
                  Quitar logo
                </button>
              )}
              {subiendoLogo && <span style={{ fontSize: 13, color: '#666' }}>Subiendo…</span>}
            </div>
            <span style={{ fontSize: 12, color: '#777' }}>Imagen cuadrada recomendada (PNG o JPG, máx. 10 MB). Se muestra en el inicio y en Asociados.</span>
          </div>

          <div className="form-group full">
            <label>Descripción</label>
            <textarea name="descripcion" value={form.descripcion} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Dirección</label>
            <input name="direccion" value={form.direccion} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Región</label>
            <select name="region" value={form.region} onChange={handleChange}>
              <option value="">Seleccione región</option>
              {REGIONES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Comuna</label>
            <select
              name="ciudad"
              value={form.ciudad}
              onChange={handleChange}
              disabled={!form.region}
            >
              <option value="">Seleccione comuna</option>
              {form.ciudad && form.region && !getComunasPorRegion(form.region).includes(form.ciudad) && (
                <option value={form.ciudad}>{form.ciudad}</option>
              )}
              {getComunasPorRegion(form.region).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Teléfono</label>
            <input name="telefono" value={form.telefono} onChange={handleChange} />
            {errors.telefono && <span className="error">{errors.telefono}</span>}
          </div>

          <div className="form-group">
            <label>Email</label>
            <input name="email" value={form.email} onChange={handleChange} />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>Sitio Web</label>
            <input name="sitio_web" value={form.sitio_web} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Estado</label>
            <select name="estado" value={form.estado} onChange={handleChange}>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="suspendido">Suspendido</option>
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading || subiendoLogo}>
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ClubFormModal;
